import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Row, Col } from 'react-bootstrap';
import {
    FiArrowLeft,
    FiCheck,
    FiPlus,
    FiX
} from 'react-icons/fi';
import Breadcrumbs from '../../components/Breadcrumbs';
import Input from '../../components/input';
import Button from '../../components/button';
import LogsComments from '../../components/LogsComments';
import UserAvatar from '../../components/UserAvatar';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { getTripDetails } from '../../store/Slices/TripSlice';
import { fetchCustomerDropdown } from '../../store/Slices/dropdownSlice';
import { apiService } from '../../services/apiService';
import { checkTripStagePermission, formatUtcDateTimeForApi, formatUtcDateTimeForInput, hasTripPermission, hasTripReachedStage, isSuperAdminRole } from '../../utils/helper';
import { TripPermission, TripsStageEnum } from '../../utils/app-constant';
import { successNotify, errorNotify } from '../../utils/toast';
import '../../styles/Trip.css';
import '../../styles/TripLoadingDispatch.css';

const getByPath = (source = {}, path = "") =>
    String(path || "")
        .split(".")
        .reduce((current, key) => (current && current[key] !== undefined ? current[key] : undefined), source);

const getFirstValue = (source = {}, paths = [], fallback = "") => {
    for (const path of paths) {
        const value = getByPath(source, path);
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }

    return fallback;
};

const getPayloadData = (response) => response?.data?.data || response?.data || response || [];

const toInputValue = (value) => (value === undefined || value === null ? "" : String(value));

const getTripCustomerId = (detail = {}) => {
    const customer = detail?.customer;

    return detail?.customer_id ||
        detail?.customerId ||
        detail?.["3pl_customer_id"] ||
        detail?.customer_info?.id ||
        detail?.customer?.id ||
        detail?.customer?.value ||
        (typeof customer === "number" || (typeof customer === "string" && !Number.isNaN(Number(customer))) ? customer : null);
};

const toNumberOrNull = (value) => {
    const normalized = String(value ?? "").replace(/,/g, "").trim();
    if (!normalized) return null;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
};

const getChecklistSavedRows = (detail = {}, paths = []) => {
    for (const path of paths) {
        const value = getByPath(detail, path);
        if (Array.isArray(value)) return value;
    }

    return [];
};

const findSavedChecklistItem = (savedRows = [], checklistId, label = "", idKeys = []) => {
    const normalizedId = String(checklistId ?? "");
    const normalizedLabel = String(label || "").trim().toLowerCase();

    return savedRows.find((row) => {
        const hasMatchingId = idKeys.some((key) => {
            const val = row?.[key];
            return val !== undefined && val !== null && String(val) === normalizedId;
        });
        const rowLabel = String(row?.label || row?.name || row?.title || row?.checklist?.label || row?.checklist?.name || "").trim().toLowerCase();
        return hasMatchingId || (normalizedLabel && rowLabel === normalizedLabel);
    });
};

const normalizeChecklistStatus = (s) => {
    const raw = String(s || '').trim().toLowerCase();
    if (raw === 'pass' || raw === 'passed') return 'pass';
    if (raw === 'fail' || raw === 'failed') return 'fail';
    return null;
};

const mapChecklistOptions = (options = [], savedRows = [], idKeys = []) =>
    (Array.isArray(options) ? options : []).map((option) => {
        const checklistId = option?.value ?? option?.id;
        const savedItem = findSavedChecklistItem(savedRows, checklistId, option?.label, idKeys);
        const status = normalizeChecklistStatus(savedItem?.status);

        return {
            id: String(checklistId ?? option?.label ?? ""),
            checklistId,
            label: option?.label || option?.name || "Checklist Item",
            type: String(option?.type || option?.category || savedItem?.type || savedItem?.checklist_item?.type || savedItem?.checklist?.type || "").trim(),
            status,
            notes: status === 'fail' ? (savedItem?.notes || savedItem?.note || "") : "",
            photo_url: savedItem?.photo_url || savedItem?.photoUrl || "",
        };
    });

const groupChecklistItems = (items = []) => {
    const list = Array.isArray(items) ? items : [];
    const hasAnyType = list.some((item) => Boolean(item.type && String(item.type).trim()));
    if (!hasAnyType) {
        return [{ groupTitle: null, items: list }];
    }

    const groupsMap = new Map();
    list.forEach((item) => {
        const rawType = item.type && String(item.type).trim();
        const typeKey = rawType || 'General';
        if (!groupsMap.has(typeKey)) {
            groupsMap.set(typeKey, []);
        }
        groupsMap.get(typeKey).push(item);
    });

    return Array.from(groupsMap.entries()).map(([groupTitle, groupItems]) => ({
        groupTitle,
        items: groupItems,
    }));
};

const buildChecklistPayload = (items = [], payloadIdKey) =>
    (Array.isArray(items) ? items : [])
        .filter((item) =>
            item?.checklistId !== undefined &&
            item?.checklistId !== null &&
            item?.checklistId !== "" &&
            (item.status === 'pass' || item.status === 'fail')
        )
        .map((item) => {
            const isFail = item.status === 'fail';
            const row = {
                [payloadIdKey]: Number(item.checklistId) || item.checklistId,
                status: item.status,
                notes: isFail ? (item.notes || "") : "",
            };

            if (item.photo_url) {
                row.photo_url = item.photo_url;
            }

            return row;
        });

const computeAllocatedFuel = (detail = {}) => {
    const rawSaved = getFirstValue(detail, [
        "allocated_fuel",
        "allocatedFuel",
        "trip.allocated_fuel",
        "trip.allocatedFuel",
    ]);

    const savedNumber = Number(String(rawSaved ?? "").replace(/,/g, "").trim());
    if (Number.isFinite(savedNumber) && savedNumber > 0) {
        return toInputValue(rawSaved);
    }

    const ownershipType = getFirstValue(detail, [
        "vehicle.ownership_type",
        "vehicle.ownershipType",
        "ownership_type",
        "ownershipType",
    ]);

    if (String(ownershipType).toLowerCase() === "hired") {
        return "0.00";
    }

    const rawDistance = getFirstValue(detail, [
        "trip_meta.total_distance",
        "trip_meta.totalDistance",
        "trip_meta.distance",
        "total_distance",
        "totalDistance",
        "distance",
        "trip_distance",
        "route_distance",
    ]);

    const rawEfficiency = getFirstValue(detail, [
        "vehicle.initial_fuel_efficiency",
        "vehicle.initialFuelEfficiency",
        "vehicle.fuel_efficiency",
        "vehicle.fuelEfficiency",
        "vehicle.current_fuel_efficiency",
        "initial_fuel_efficiency",
        "initialFuelEfficiency",
        "fuel_efficiency",
    ]);

    const distance = Number(String(rawDistance ?? "").replace(/,/g, "").trim());
    const efficiency = Number(String(rawEfficiency ?? "").replace(/,/g, "").trim());

    if (Number.isFinite(distance) && distance > 0 && Number.isFinite(efficiency) && efficiency > 0) {
        const calculated = distance / efficiency;
        return String(parseFloat(calculated.toFixed(2)));
    }

    if (rawSaved !== undefined && rawSaved !== null && rawSaved !== "") {
        return toInputValue(rawSaved);
    }

    return "";
};

const computeOpeningFuel = (detail = {}) => {
    const rawDirect = getFirstValue(detail, [
        "opening_fuel",
        "openingFuel",
    ]);

    const rawVehicleFuel = getFirstValue(detail, [
        "vehicle.current_fuel",
        "vehicle.currentFuel",
        "vehicle_info.current_fuel",
        "vehicle_info.currentFuel",
        "current_fuel",
        "currentFuel",
        "vehicle.fuel_volume",
    ]);

    const directNum = Number(String(rawDirect ?? "").replace(/,/g, "").trim());
    if (rawDirect !== undefined && rawDirect !== null && rawDirect !== "" && Number.isFinite(directNum) && directNum > 0) {
        return toInputValue(rawDirect);
    }

    if (rawVehicleFuel !== undefined && rawVehicleFuel !== null && rawVehicleFuel !== "") {
        return toInputValue(rawVehicleFuel);
    }

    if (rawDirect !== undefined && rawDirect !== null && rawDirect !== "") {
        return toInputValue(rawDirect);
    }

    return "";
};

const loadingDispatchValidationSchema = Yup.object().shape({});

const renderChecklistBadge = (items, selectedCount, totalCount, remainingCount) => {
    const isAllSelected = totalCount > 0 && selectedCount === totalCount;
    if (!isAllSelected) {
        return (
            <>
                <span className="badge_red_pill">{selectedCount}/{totalCount}</span>
                <span className="badge_red_text">{remainingCount} remaining</span>
            </>
        );
    }

    const hasFail = items.some((i) => i.status === 'fail');
    if (hasFail) {
        return <span className="badge_attention_pill">Has Issues</span>;
    }

    return <span className="badge_green_pill">Completed</span>;
};

const TripLoadingDispatch = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();

    const realTripId = useMemo(() => {
        if (!id) return null;
        return String(id).replace(/^TR-/i, '');
    }, [id]);

    const { tripDetailsData, tripDetailsLoading } = useSelector((state) => state.trips);
    const { customerDropdownData } = useSelector((state) => state.dropdowns || state.dropdown || {});
    const detailData = useMemo(() => tripDetailsData?.data || tripDetailsData || {}, [tripDetailsData]);

    const [vehicleChecklistOptions, setVehicleChecklistOptions] = useState([]);
    const [driverDocChecklistOptions, setDriverDocChecklistOptions] = useState([]);
    const [checklistLoading, setChecklistLoading] = useState(true);
    const [tripDetailsRequestPending, setTripDetailsRequestPending] = useState(() => Boolean(realTripId));
    const [loadedTripDetailsId, setLoadedTripDetailsId] = useState(null);
    const [fetchedCustomerName, setFetchedCustomerName] = useState("");

    const [vehicleItems, setVehicleItems] = useState([]);
    const [driverDocsItems, setDriverDocsItems] = useState([]);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [editOpeningFuel, setEditOpeningFuel] = useState('');
    const [editAllocatedFuel, setEditAllocatedFuel] = useState('');
    const [editAllocatedPettyCash, setEditAllocatedPettyCash] = useState('');

    const canSaveLoadingDispatch = useMemo(() => {
        return (
            isSuperAdminRole() ||
            hasTripPermission([
                TripPermission.ChecklistsAndAllocations,
                TripPermission.ReadyToDispatch,
                TripPermission.TripStops,
                TripPermission.Update,
                TripPermission.Create,
            ])
        );
    }, []);

    // Once the trip has completed, this stage's data is
    // locked for editing - still viewable via Back, but Save must stay disabled.
    const isTripCompleted = useMemo(() => {
        const status = String(detailData?.status || '').toLowerCase().trim();
        const stage = String(detailData?.stage || '').toLowerCase().trim();
        return (
            status === 'completed' ||
            status === 'closed' ||
            stage === 'completed' ||
            stage === 'closed' ||
            hasTripReachedStage(detailData?.stage, TripsStageEnum.Completed)
        );
    }, [detailData?.status, detailData?.stage]);

    const isTripPastInTransit = isTripCompleted;

    const handleBackToPlanning = () => {
        const previousStageCheck = checkTripStagePermission("planning");
        if (previousStageCheck.allowed) {
            navigate(`/trips-list/planning/${dispatchTripId}`);
        } else {
            errorNotify("You do not have permission to access Trip Planning");
        }
    };

    useEffect(() => {
        let isActive = true;

        if (!realTripId) {
            setTripDetailsRequestPending(false);
            setLoadedTripDetailsId(null);
            return () => {
                isActive = false;
            };
        }

        setTripDetailsRequestPending(true);
        dispatch(getTripDetails(realTripId)).finally(() => {
            if (isActive) {
                setLoadedTripDetailsId(realTripId);
                setTripDetailsRequestPending(false);
            }
        });
        dispatch(fetchCustomerDropdown());

        return () => {
            isActive = false;
        };
    }, [dispatch, realTripId]);

    useEffect(() => {
        let isActive = true;

        const fetchChecklists = async () => {
            setChecklistLoading(true);
            try {
                const [vehicleResponse, driverResponse] = await Promise.all([
                    apiService.getVehicleInspectionChecklistDropdown(),
                    apiService.getDriverDocChecklistDropdown(),
                ]);

                if (!isActive) return;

                setVehicleChecklistOptions(getPayloadData(vehicleResponse));
                const rawDocs = getPayloadData(driverResponse);
                const hasPod = (Array.isArray(rawDocs) ? rawDocs : []).some(d => String(d?.label || d?.title || '').toLowerCase().includes('pod') || String(d?.label || d?.title || '').toLowerCase().includes('proof of delivery'));
                const finalDocs = hasPod ? rawDocs : [...(Array.isArray(rawDocs) ? rawDocs : []), { value: 6, label: 'Proof of Delivery (POD)' }];
                setDriverDocChecklistOptions(finalDocs);
            } catch (err) {
                if (isActive) {
                    errorNotify(err || "Failed to fetch checklists");
                    setVehicleChecklistOptions([]);
                    setDriverDocChecklistOptions([]);
                }
            } finally {
                if (isActive) {
                    setChecklistLoading(false);
                }
            }
        };

        fetchChecklists();

        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        let isActive = true;

        const fetchCustomerDetailData = async () => {
            const custId = getTripCustomerId(detailData);
            setFetchedCustomerName("");

            if (!custId) return;

            try {
                const response = await apiService.getCustomerTripDetail(custId);
                const data = response?.data || response || {};
                const customer = detailData?.customer;
                const cName = data?.name ||
                    data?.customer_name ||
                    data?.company_name ||
                    data?.title ||
                    (typeof customer === "object" ? customer?.name : "") ||
                    (typeof customer === "string" && Number.isNaN(Number(customer)) ? customer : "");

                if (isActive && cName) {
                    setFetchedCustomerName(cName);
                }
            } catch (err) {
                console.error("Error fetching customer trip detail:", err);
            }
        };

        fetchCustomerDetailData();

        return () => {
            isActive = false;
        };
    }, [detailData]);

    useEffect(() => {
        const savedVehicleRows = getChecklistSavedRows(detailData, [
            "vehicle_inspection_checklist",
            "vehicle_inspection_checklists",
            "vehicleInspectionChecklist",
            "trip_vehicle_inspection_checklist",
        ]);
        setVehicleItems(mapChecklistOptions(vehicleChecklistOptions, savedVehicleRows, [
            "vehicle_inspection_checklist_id",
            "checklist_id",
        ]));
    }, [detailData, vehicleChecklistOptions]);

    useEffect(() => {
        const savedDriverRows = getChecklistSavedRows(detailData, [
            "driver_doc_checklist",
            "driver_doc_checklists",
            "driverDocumentChecklist",
            "trip_driver_doc_checklist",
        ]);
        setDriverDocsItems(mapChecklistOptions(driverDocChecklistOptions, savedDriverRows, [
            "driver_doc_checklist_id",
            "checklist_id",
        ]));
    }, [detailData, driverDocChecklistOptions]);

    useEffect(() => {
        setEditOpeningFuel(String(computeOpeningFuel(detailData) ?? ''));
        setEditAllocatedFuel(String(computeAllocatedFuel(detailData) ?? ''));
        setEditAllocatedPettyCash(String(getFirstValue(detailData, ["allocated_petty_cash", "allocatedPettyCash"]) ?? ''));
    }, [detailData]);

    const tripIdDisplay = detailData?.trip_id || id || "--";
    const dispatchTripId = realTripId || detailData?.id || id;
    const resolvedTripId = Number(detailData?.id || realTripId || id);
    const resolvedVehicleId = Number(
        detailData?.vehicle_id ||
        detailData?.vehicle?.id ||
        detailData?.vehicleId ||
        0
    );

    const customerName = useMemo(() => {
        const custObj = detailData?.customer;
        if (typeof custObj === "object" && custObj !== null) {
            const nameStr = custObj.name || custObj.label || custObj.title || custObj.customer_name || custObj.company_name;
            if (nameStr) return nameStr;
        }

        const custId = getTripCustomerId(detailData);
        if (Array.isArray(customerDropdownData) && customerDropdownData.length > 0 && custId) {
            const foundCust = customerDropdownData.find(c => String(c.value || c.id) === String(custId));
            if (foundCust) return foundCust.label || foundCust.name || foundCust.title;
        }

        return fetchedCustomerName ||
            (typeof detailData?.customer_name === "string" ? detailData.customer_name : "") ||
            (typeof custObj === "string" && Number.isNaN(Number(custObj)) ? custObj : "") ||
            "--";
    }, [detailData, customerDropdownData, fetchedCustomerName]);

    const vehicleLabel = useMemo(() => {
        const vehicle = detailData?.vehicle;
        const registration = vehicle?.registration_number || vehicle?.reg_no || detailData?.vehicle_registration_number || detailData?.vehicle_id;
        const type = detailData?.vehicle_type?.name || detailData?.vehicle_type_name || vehicle?.vehicle_type?.name || vehicle?.type;

        return [registration || "--", type ? `(${type})` : ""].filter(Boolean).join(" ");
    }, [detailData]);

    const driverName = detailData?.primary_driver_name || detailData?.primary_driver?.name || detailData?.primary_driver?.username || "--";
    const driverPhone = detailData?.primary_driver_contact ? `+${detailData.primary_driver_contact}` : "--";
    const driverImage = detailData?.primary_driver?.image || detailData?.primary_driver?.avatar || null;

    const handleVehicleStatusToggle = (id, targetStatus) => {
        setVehicleItems(prev => prev.map(item => {
            if (item.id === id) {
                const newStatus = item.status === targetStatus ? null : targetStatus;
                return { ...item, status: newStatus, ...(newStatus !== 'fail' ? { notes: '' } : {}) };
            }
            return item;
        }));
    };

    const handleDriverDocStatusToggle = (id, targetStatus) => {
        setDriverDocsItems(prev => prev.map(item => {
            if (item.id === id) {
                const newStatus = item.status === targetStatus ? null : targetStatus;
                return { ...item, status: newStatus, ...(newStatus !== 'fail' ? { notes: '' } : {}) };
            }
            return item;
        }));
    };

    const handleVehicleNoteChange = (id, newNote) => {
        setVehicleItems(prev => prev.map(item => {
            if (item.id === id) {
                return { ...item, notes: newNote };
            }
            return item;
        }));
    };

    const handleDriverDocNoteChange = (id, newNote) => {
        setDriverDocsItems(prev => prev.map(item => {
            if (item.id === id) {
                return { ...item, notes: newNote };
            }
            return item;
        }));
    };

    const handleGroupSelectAll = (isVehicle, groupItems = [], targetStatus) => {
        const itemIds = new Set(groupItems.map(i => i.id));
        const isAllAlreadyTarget = groupItems.length > 0 && groupItems.every(i => i.status === targetStatus);
        const nextStatus = isAllAlreadyTarget ? null : targetStatus;

        const updater = (prev) =>
            prev.map((item) => {
                if (itemIds.has(item.id)) {
                    return {
                        ...item,
                        status: nextStatus,
                        ...(nextStatus !== 'fail' ? { notes: '' } : {}),
                    };
                }
                return item;
            });

        if (isVehicle) {
            setVehicleItems(updater);
        } else {
            setDriverDocsItems(updater);
        }
    };

    const handleSectionSelectAll = (sectionKey, targetStatus) => {
        const isVehicle = sectionKey === 'vehicle';
        const currentItems = isVehicle ? vehicleItems : driverDocsItems;
        const isAllAlreadyTarget = currentItems.length > 0 && currentItems.every(i => i.status === targetStatus);
        const nextStatus = isAllAlreadyTarget ? null : targetStatus;

        const updater = (prev) =>
            prev.map((item) => ({
                ...item,
                status: nextStatus,
                ...(nextStatus !== 'fail' ? { notes: '' } : {}),
            }));

        if (isVehicle) {
            setVehicleItems(updater);
        } else {
            setDriverDocsItems(updater);
        }
    };

    const isValidStatus = (st) => st === 'pass' || st === 'fail';

    const groupedVehicleItems = useMemo(() => groupChecklistItems(vehicleItems), [vehicleItems]);
    const groupedDriverDocsItems = useMemo(() => groupChecklistItems(driverDocsItems), [driverDocsItems]);

    const vehicleSelectedCount = vehicleItems.filter(i => isValidStatus(i.status)).length;
    const vehicleTotalCount = vehicleItems.length;
    const vehicleRemainingCount = vehicleTotalCount - vehicleSelectedCount;
    const vehicleHasFail = vehicleItems.some(i => i.status === 'fail');
    const vehicleIsAllSelected = vehicleTotalCount > 0 && vehicleSelectedCount === vehicleTotalCount;

    const driverDocsSelectedCount = driverDocsItems.filter(i => isValidStatus(i.status)).length;
    const driverDocsTotalCount = driverDocsItems.length;
    const driverDocsRemainingCount = driverDocsTotalCount - driverDocsSelectedCount;
    const driverDocsHasFail = driverDocsItems.some(i => i.status === 'fail');
    const driverDocsIsAllSelected = driverDocsTotalCount > 0 && driverDocsSelectedCount === driverDocsTotalCount;


    const initialValues = useMemo(() => ({
        general_notes: toInputValue(getFirstValue(detailData, ["loading_dispatch_notes", "dispatch_notes", "notes", "note"])),
    }), [detailData]);

    const handleFormSubmit = async (values, { setSubmitting }) => {
        setShowValidationErrors(true);

        if (!canSaveLoadingDispatch) {
            errorNotify("You do not have permission to save Loading & Dispatch stage");
            setSubmitting(false);
            return;
        }

        if (isTripCompleted) {
            errorNotify("This trip is already completed and can no longer be edited");
            setSubmitting(false);
            return;
        }

        const unselectedVehicle = vehicleItems.filter((i) => !isValidStatus(i.status));
        const unselectedDriverDocs = driverDocsItems.filter((i) => !isValidStatus(i.status));
        const totalUnselected = unselectedVehicle.length + unselectedDriverDocs.length;

        if (totalUnselected > 0) {
            errorNotify(`Please select all checklist items (${totalUnselected} remaining)`);
            setSubmitting(false);
            setTimeout(() => {
                const firstErrorElem = document.querySelector('.has_unselected_error, .checklist_section_card.has_section_error');
                if (firstErrorElem) {
                    firstErrorElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 50);
            return;
        }

        try {
            const payload = {
                trip_id: Number(dispatchTripId) || dispatchTripId,
                notes: values.general_notes || "",
                driver_doc_checklist: buildChecklistPayload(driverDocsItems, "driver_doc_checklist_id"),
                vehicle_inspection_checklist: buildChecklistPayload(vehicleItems, "vehicle_inspection_checklist_id"),
            };

            await apiService.updateChecklistsAndAllocations(payload);
            successNotify("Loading & Dispatch details saved successfully!");
            if (dispatchTripId) {
                await dispatch(getTripDetails(dispatchTripId));
            }
            const nextStageCheck = checkTripStagePermission("ready-to-dispatch");
            if (nextStageCheck.allowed) {
                navigate(`/trips-list/ready-dispatch/${dispatchTripId}`, { replace: true });
            } else {
                navigate("/trips-list", { replace: true });
            }
        } catch (err) {
            errorNotify(err || "Failed to save Loading & Dispatch details");
        } finally {
            setSubmitting(false);
        }
    };

    const isPageLoading = Boolean(
        checklistLoading ||
        (realTripId && (tripDetailsLoading || tripDetailsRequestPending || loadedTripDetailsId !== realTripId))
    );

    if (isPageLoading) {
        return <SpinnerLoader />;
    }

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={loadingDispatchValidationSchema}
                enableReinitialize={true}
                onSubmit={handleFormSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, handleSubmit }) => (
                    <Form className="trip_loading_dispatch_page">
                        {/* Desktop Header */}
                        <div className="desktop_header_only">
                            <Breadcrumbs values={["Trip Management", "Trip Detail", "Loading & Dispatch"]} />
                            <div className="access_view dispatch_top_bar">
                                <h2>Loading & Dispatch</h2>
                                <div className="dispatch_top_actions">
                                    <Button
                                        cancelBtn={handleBackToPlanning}
                                        cancelBtnText="Back"
                                        loading={isSubmitting}
                                        disabled={isSubmitting || isTripPastInTransit}
                                        submitText={isSubmitting ? "Saving..." : "Save"}
                                        type="button"
                                        shouldRow={false}
                                        isShowSubmitBtn={canSaveLoadingDispatch}
                                        submitBtn={handleSubmit}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Mobile Header */}
                        <div className="mobile_header_only mobile_dispatch_header">
                            <div className="mobile_header_left_controls">
                                <button
                                    className="mobile_menu_icon_btn"
                                    onClick={handleBackToPlanning}
                                    type="button"
                                    title="Back"
                                >
                                    <FiArrowLeft style={{ fontSize: '20px' }} />
                                </button>
                                <h1 className="mobile_header_title">Loading & Dispatch</h1>
                            </div>
                            <div className="mobile_header_right_controls">
                                {canSaveLoadingDispatch && (
                                    <button className="btn_figma_primary" type="submit" disabled={isSubmitting || isTripPastInTransit}>
                                        {isSubmitting ? "Saving..." : "Save"}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Main Grid Row Layout */}
                        <Row className="dispatch_main_row g-4">
                            {/* LEFT COLUMN: Checklists and Allocations */}
                            <Col lg={8} md={12} className="dispatch_left_col">
                                <div className="checklists_allocations_card">
                                    {/* Outer Card Header */}
                                    <div className="checklists_card_header">
                                        <div>
                                            <h3 className="title">Checklists and Allocations</h3>
                                            <p className="subtitle">Review pre-trip checks & allocations before starting the journey.</p>
                                        </div>
                                        <div className="trip_id_banner_pill">
                                            Trip ID: {tripIdDisplay}
                                        </div>
                                    </div>

                                    {/* Customer / Vehicle / Driver Metadata Card */}
                                    <div className="trip_meta_info_card">
                                        <div className="meta_info_item">
                                            <span className="meta_info_label">Customer</span>
                                            <span className="meta_info_value">{customerName}</span>
                                        </div>
                                        <div className="meta_info_item">
                                            <span className="meta_info_label">Vehicle</span>
                                            <span className="meta_info_value">
                                                <a href="#vehicle" className="meta_info_value_link">{vehicleLabel}</a>
                                            </span>
                                        </div>
                                        <div className="meta_info_item">
                                            <span className="meta_info_label">Driver</span>
                                            <div className="driver_meta_row">
                                                <UserAvatar username={driverName} image={driverImage} size={28} />
                                                <span className="driver_name_link">{driverName}</span>
                                            </div>
                                        </div>
                                        <div className="meta_info_item">
                                            <span className="meta_info_label">Driver Contact</span>
                                            <span className="meta_info_value">{driverPhone}</span>
                                        </div>
                                    </div>



                                    {/* Section 1: Vehicle Inspection Checklist */}
                                    <div className={`checklist_section_card ${showValidationErrors && vehicleRemainingCount > 0 ? 'has_section_error' : ''}`}>
                                        <div className="checklist_section_header">
                                            <h4 className="checklist_section_title">Vehicle Inspection Checklist</h4>
                                            <div className="header_badge_group">
                                                <div className="checklist_select_all_group section_select_all">
                                                    <span className="select_all_label">All:</span>
                                                    <button
                                                        type="button"
                                                        className={`select_all_btn select_all_pass ${vehicleItems.length > 0 && vehicleItems.every(i => i.status === 'pass') ? 'active' : ''}`}
                                                        onClick={() => handleSectionSelectAll('vehicle', 'pass')}
                                                        title="Select all vehicle checklist items as Pass"
                                                    >
                                                        <FiCheck /> Pass All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`select_all_btn select_all_fail ${vehicleItems.length > 0 && vehicleItems.every(i => i.status === 'fail') ? 'active' : ''}`}
                                                        onClick={() => handleSectionSelectAll('vehicle', 'fail')}
                                                        title="Select all vehicle checklist items as Fail"
                                                    >
                                                        <FiX /> Fail All
                                                    </button>
                                                </div>
                                                {renderChecklistBadge(vehicleItems, vehicleSelectedCount, vehicleTotalCount, vehicleRemainingCount)}
                                            </div>
                                        </div>

                                        {showValidationErrors && vehicleRemainingCount > 0 && (
                                            <div className="checklist_section_error_banner">
                                                Please select all items in Vehicle Inspection Checklist ({vehicleRemainingCount} remaining)
                                            </div>
                                        )}

                                        <div className="checklist_items_container">
                                            {groupedVehicleItems.map((group, groupIdx) => {
                                                const groupCompletedCount = group.items.filter((i) => isValidStatus(i.status)).length;
                                                const groupTotal = group.items.length;
                                                const isGroupCompleted = groupTotal > 0 && groupCompletedCount === groupTotal;
                                                const groupHasFail = group.items.some((i) => i.status === 'fail');
                                                const isGroupAllPass = groupTotal > 0 && group.items.every((i) => i.status === 'pass');
                                                const isGroupAllFail = groupTotal > 0 && group.items.every((i) => i.status === 'fail');

                                                return (
                                                    <div key={group.groupTitle || `v-group-${groupIdx}`} className="checklist_type_group">
                                                        {group.groupTitle && (
                                                            <div className="checklist_type_header">
                                                                <div className="checklist_type_title_wrap">
                                                                    <span className="checklist_type_dot" />
                                                                    <h5 className="checklist_type_title">{group.groupTitle}</h5>
                                                                </div>
                                                                <div className="checklist_type_actions_wrap">
                                                                    <div className="checklist_select_all_group">
                                                                        <button
                                                                            type="button"
                                                                            className={`select_all_btn select_all_pass ${isGroupAllPass ? 'active' : ''}`}
                                                                            onClick={() => handleGroupSelectAll(true, group.items, 'pass')}
                                                                            title={`Pass all ${group.groupTitle} items`}
                                                                        >
                                                                            <FiCheck /> Pass All
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className={`select_all_btn select_all_fail ${isGroupAllFail ? 'active' : ''}`}
                                                                            onClick={() => handleGroupSelectAll(true, group.items, 'fail')}
                                                                            title={`Fail all ${group.groupTitle} items`}
                                                                        >
                                                                            <FiX /> Fail All
                                                                        </button>
                                                                    </div>
                                                                    <span
                                                                        className={`checklist_type_counter ${groupHasFail ? 'has_fail' : isGroupCompleted ? 'completed' : ''
                                                                            }`}
                                                                    >
                                                                        {groupCompletedCount}/{groupTotal}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="checklist_type_items">
                                                            {group.items.map((item) => (
                                                                <React.Fragment key={item.id}>
                                                                    <div className={`checklist_item_row ${showValidationErrors && !item.status ? 'has_unselected_error' : ''}`}>
                                                                        <div className="checklist_item_left">
                                                                            <span className="checklist_item_title">{item.label}</span>
                                                                            {showValidationErrors && !item.status && (
                                                                                <span className="checklist_item_unselected_warning">* Required</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="checklist_action_buttons">
                                                                            <button
                                                                                type="button"
                                                                                className={`chk_btn chk_btn_pass ${item.status === 'pass' ? 'active' : ''}`}
                                                                                onClick={() => handleVehicleStatusToggle(item.id, 'pass')}
                                                                                title="Pass"
                                                                            >
                                                                                <FiCheck />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`chk_btn chk_btn_fail ${item.status === 'fail' ? 'active' : ''}`}
                                                                                onClick={() => handleVehicleStatusToggle(item.id, 'fail')}
                                                                                title="Fail"
                                                                            >
                                                                                <FiX />
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Note alert box - only show when status is fail */}
                                                                    {item.status === 'fail' && (
                                                                        <div className="checklist_note_box">
                                                                            <input
                                                                                type="text"
                                                                                className="note_input_clean"
                                                                                value={item.notes || ""}
                                                                                onChange={(e) => handleVehicleNoteChange(item.id, e.target.value)}
                                                                                placeholder="Add reason for failure"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Section 2: Driver Documents Checklist */}
                                    <div className={`checklist_section_card ${showValidationErrors && driverDocsRemainingCount > 0 ? 'has_section_error' : ''}`}>
                                        <div className="checklist_section_header">
                                            <h4 className="checklist_section_title">Driver Documents Checklist</h4>
                                            <div className="header_badge_group">
                                                <div className="checklist_select_all_group section_select_all">
                                                    <span className="select_all_label">All:</span>
                                                    <button
                                                        type="button"
                                                        className={`select_all_btn select_all_pass ${driverDocsItems.length > 0 && driverDocsItems.every(i => i.status === 'pass') ? 'active' : ''}`}
                                                        onClick={() => handleSectionSelectAll('driverDocs', 'pass')}
                                                        title="Select all driver document checklist items as Pass"
                                                    >
                                                        <FiCheck /> Pass All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`select_all_btn select_all_fail ${driverDocsItems.length > 0 && driverDocsItems.every(i => i.status === 'fail') ? 'active' : ''}`}
                                                        onClick={() => handleSectionSelectAll('driverDocs', 'fail')}
                                                        title="Select all driver document checklist items as Fail"
                                                    >
                                                        <FiX /> Fail All
                                                    </button>
                                                </div>
                                                {renderChecklistBadge(driverDocsItems, driverDocsSelectedCount, driverDocsTotalCount, driverDocsRemainingCount)}
                                            </div>
                                        </div>

                                        {showValidationErrors && driverDocsRemainingCount > 0 && (
                                            <div className="checklist_section_error_banner">
                                                Please select all items in Driver Documents Checklist ({driverDocsRemainingCount} remaining)
                                            </div>
                                        )}

                                        <div className="checklist_items_container">
                                            {groupedDriverDocsItems.map((group, groupIdx) => {
                                                const groupCompletedCount = group.items.filter((i) => isValidStatus(i.status)).length;
                                                const groupTotal = group.items.length;
                                                const isGroupCompleted = groupTotal > 0 && groupCompletedCount === groupTotal;
                                                const groupHasFail = group.items.some((i) => i.status === 'fail');
                                                const isGroupAllPass = groupTotal > 0 && group.items.every((i) => i.status === 'pass');
                                                const isGroupAllFail = groupTotal > 0 && group.items.every((i) => i.status === 'fail');

                                                return (
                                                    <div key={group.groupTitle || `d-group-${groupIdx}`} className="checklist_type_group">
                                                        {group.groupTitle && (
                                                            <div className="checklist_type_header">
                                                                <div className="checklist_type_title_wrap">
                                                                    <span className="checklist_type_dot" />
                                                                    <h5 className="checklist_type_title">{group.groupTitle}</h5>
                                                                </div>
                                                                <div className="checklist_type_actions_wrap">
                                                                    <div className="checklist_select_all_group">
                                                                        <button
                                                                            type="button"
                                                                            className={`select_all_btn select_all_pass ${isGroupAllPass ? 'active' : ''}`}
                                                                            onClick={() => handleGroupSelectAll(false, group.items, 'pass')}
                                                                            title={`Pass all ${group.groupTitle} items`}
                                                                        >
                                                                            <FiCheck /> Pass All
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className={`select_all_btn select_all_fail ${isGroupAllFail ? 'active' : ''}`}
                                                                            onClick={() => handleGroupSelectAll(false, group.items, 'fail')}
                                                                            title={`Fail all ${group.groupTitle} items`}
                                                                        >
                                                                            <FiX /> Fail All
                                                                        </button>
                                                                    </div>
                                                                    <span
                                                                        className={`checklist_type_counter ${groupHasFail ? 'has_fail' : isGroupCompleted ? 'completed' : ''
                                                                            }`}
                                                                    >
                                                                        {groupCompletedCount}/{groupTotal}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="checklist_type_items">
                                                            {group.items.map((item) => (
                                                                <React.Fragment key={item.id}>
                                                                    <div className={`checklist_item_row ${showValidationErrors && !item.status ? 'has_unselected_error' : ''}`}>
                                                                        <div className="checklist_item_left">
                                                                            <span className="checklist_item_title">{item.label}</span>
                                                                            {showValidationErrors && !item.status && (
                                                                                <span className="checklist_item_unselected_warning">* Required</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="checklist_action_buttons">
                                                                            <button
                                                                                type="button"
                                                                                className={`chk_btn chk_btn_pass ${item.status === 'pass' ? 'active' : ''}`}
                                                                                onClick={() => handleDriverDocStatusToggle(item.id, 'pass')}
                                                                                title="Pass"
                                                                            >
                                                                                <FiCheck />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`chk_btn chk_btn_fail ${item.status === 'fail' ? 'active' : ''}`}
                                                                                onClick={() => handleDriverDocStatusToggle(item.id, 'fail')}
                                                                                title="Fail"
                                                                            >
                                                                                <FiX />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {/* Note alert box - only show when status is fail */}
                                                                    {item.status === 'fail' && (
                                                                        <div className="checklist_note_box">
                                                                            <input
                                                                                type="text"
                                                                                className="note_input_clean"
                                                                                value={item.notes || ""}
                                                                                onChange={(e) => handleDriverDocNoteChange(item.id, e.target.value)}
                                                                                placeholder="Add reason for failure"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                </div>
                            </Col>

                            {/* RIGHT COLUMN: Fuel & Cash, LogsComments, General Notes */}
                            <Col lg={4} md={12} className="dispatch_right_col">
                                {/* Card 1: Logs & Comments Widget */}
                                <div className="driver_details_sidebar service_cost_summary_details mb-3">
                                    <LogsComments />
                                </div>

                                {/* Card 2: General Notes */}
                                <div className="trip_notes_main_div">
                                    <h5>General Notes</h5>
                                    <div style={{ padding: '0 16px 16px 16px' }}>
                                        <Input
                                            label=""
                                            name="general_notes"
                                            placeholder="Type your notes or additional info..."
                                            quill={true}
                                            value={values.general_notes}
                                            setFieldValue={setFieldValue}
                                            onBlur={handleBlur}
                                            error={errors.general_notes}
                                            touched={touched.general_notes}
                                            required={false}
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Formik>

        </>
    );
};

export default TripLoadingDispatch;
